/**
 * useFinancing Hook
 * Hook para gerenciar contas de financiamento e utilizações
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { BankAccount, FinancingUtilization, FinancingUtilizationFormData } from '../types/configuration';
import { toast } from 'sonner';

interface UseFinancingOptions {
  companyIds: string[];
  userId: string;
}

export function useFinancing({ companyIds, userId }: UseFinancingOptions) {
  const queryClient = useQueryClient();

  // Buscar contas de financiamento
  const { data: financingAccounts = [], isLoading: accountsLoading } = useQuery({
    queryKey: ['financing-accounts', companyIds],
    queryFn: async () => {
      if (!companyIds.length) return [];

      const { data, error } = await supabase
        .from('bank_accounts')
        .select('*, bank:banks(*)')
        .eq('account_type', 'financing')
        .in('company_id', companyIds)
        .eq('is_active', true)
        .order('account_name');

      if (error) throw error;
      return data as BankAccount[];
    },
    enabled: companyIds.length > 0,
  });

  // Buscar utilizações
  const { data: utilizations = [], isLoading: utilizationsLoading } = useQuery({
    queryKey: ['financing-utilizations', companyIds],
    queryFn: async () => {
      if (!companyIds.length) return [];

      const { data, error } = await supabase
        .from('financing_utilizations')
        .select('*, bank_account:bank_accounts(account_name)')
        .in('company_id', companyIds)
        .order('utilization_date', { ascending: false });

      if (error) throw error;
      return data as FinancingUtilization[];
    },
    enabled: companyIds.length > 0,
  });

  // Criar utilização de crédito
  const createUtilization = useMutation({
    mutationFn: async ({
      bankAccountId,
      companyId,
      data,
    }: {
      bankAccountId: string;
      companyId: string;
      data: FinancingUtilizationFormData;
    }) => {
      // 1. Buscar conta atual do banco de dados para ter dados atualizados
      const { data: account, error: accountError } = await supabase
        .from('bank_accounts')
        .select('*')
        .eq('id', bankAccountId)
        .single();

      if (accountError || !account) {
        throw new Error('Conta de financiamento não encontrada no banco de dados');
      }

      // 2. Criar registro de utilização
      const { data: utilization, error: utilizationError } = await supabase
        .from('financing_utilizations')
        .insert({
          bank_account_id: bankAccountId,
          company_id: companyId,
          user_id: userId,
          amount: data.amount,
          description: data.description,
          utilization_date: data.utilization_date,
        })
        .select()
        .single();

      if (utilizationError) throw utilizationError;

      // 3. Atualizar financing_used na conta
      const newUsed = (account.financing_used || 0) + data.amount;

      const { error: updateError } = await supabase
        .from('bank_accounts')
        .update({ financing_used: newUsed })
        .eq('id', bankAccountId);

      if (updateError) throw updateError;

      return utilization;
    },
    onSuccess: () => {
      // Invalidar todas as queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['financing-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['financing-utilizations'] });
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
      toast.success('Crédito utilizado com sucesso');
    },
    onError: (error: any) => {
      toast.error(`Erro ao utilizar crédito: ${error.message}`);
    },
  });

  // Deletar utilização
  const deleteUtilization = useMutation({
    mutationFn: async (utilizationId: string) => {
      // 1. Buscar a utilização para pegar o valor
      const { data: utilization, error: fetchError } = await supabase
        .from('financing_utilizations')
        .select('*, bank_account_id, amount')
        .eq('id', utilizationId)
        .single();

      if (fetchError) throw fetchError;

      // 2. Deletar utilização
      const { error: deleteError } = await supabase
        .from('financing_utilizations')
        .delete()
        .eq('id', utilizationId);

      if (deleteError) throw deleteError;

      // 3. Atualizar financing_used na conta
      const account = financingAccounts.find((a) => a.id === utilization.bank_account_id);
      if (account) {
        const newUsed = Math.max(0, (account.financing_used || 0) - utilization.amount);

        const { error: updateError } = await supabase
          .from('bank_accounts')
          .update({ financing_used: newUsed })
          .eq('id', utilization.bank_account_id);

        if (updateError) throw updateError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financing-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['financing-utilizations'] });
      toast.success('Utilização removida com sucesso');
    },
    onError: (error: any) => {
      toast.error(`Erro ao remover utilização: ${error.message}`);
    },
  });

  // Calcular totais
  const totals = {
    totalLimit: financingAccounts.reduce((sum, acc) => sum + (acc.financing_limit || 0), 0),
    totalUsed: financingAccounts.reduce((sum, acc) => sum + (acc.financing_used || 0), 0),
    totalAvailable: financingAccounts.reduce(
      (sum, acc) => sum + ((acc.financing_limit || 0) - (acc.financing_used || 0)),
      0
    ),
  };

  return {
    financingAccounts,
    utilizations,
    totals,
    loading: accountsLoading || utilizationsLoading,
    createUtilization: createUtilization.mutate,
    deleteUtilization: deleteUtilization.mutate,
    isCreating: createUtilization.isPending,
    isDeleting: deleteUtilization.isPending,
  };
}

